import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Minimal product snapshot needed to render a recently-viewed card. */
export interface RecentlyViewedEntry {
    id: string;
    slug: string;
    title: string;
    primary_image: string;
    price: number;
    // Optional/nullable: product details may omit these (no sale / no reviews).
    // recentEntryToRelatedProduct coalesces them when mapping to a card.
    compare_at_price?: number | null;
    discount_percentage: string;
    rating?: number | null;
    review_count?: number | null;
    brand_name: string;
}

/** Cap so the carousel/localStorage stay small; oldest entries drop off. */
const MAX_RECENTLY_VIEWED = 12;

interface RecentlyViewedState {
    items: RecentlyViewedEntry[];
    /** Record a product view: de-dupes by id and moves it to the front (most recent first). */
    addRecentlyViewed: (entry: RecentlyViewedEntry) => void;
    clear: () => void;
}

export const useRecentlyViewedStore = create<RecentlyViewedState>()(
    persist(
        (set) => ({
            items: [],
            addRecentlyViewed: (entry) => {
                if (!entry?.id) return;
                set((state) => {
                    const withoutDuplicate = state.items.filter((i) => i.id !== entry.id);
                    return { items: [entry, ...withoutDuplicate].slice(0, MAX_RECENTLY_VIEWED) };
                });
            },
            clear: () => set({ items: [] }),
        }),
        {
            name: 'recently-viewed-storage',
            onRehydrateStorage: () => (state) => {
                if (state && !Array.isArray(state.items)) {
                    console.warn('Detected corrupted recently-viewed state, resetting to empty array');
                    state.items = [];
                }
            },
        }
    )
);
